// File: src/components/admin/OTPPhoneView.js
// Copyright (c) 2025 DefoAI UG (haftungsbeschränkt)
// All rights reserved.
//
// Admin OTP Phone Number Management View

import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    Box, Paper, Typography, TextField, Button, Table, TableBody, TableCell,
    TableContainer, TableHead, TableRow, Chip, IconButton, Grid,
    CircularProgress, Stack, Alert, Dialog, DialogTitle, DialogContent,
    DialogActions, Snackbar, Tooltip, FormControl, InputLabel, Select, MenuItem,
} from '@mui/material';
import {
    Refresh as RefreshIcon, Search as SearchIcon, Edit as EditIcon,
    Delete as DeleteIcon, Phone as PhoneIcon, Visibility as ViewIcon,
    PersonRemove as ReleaseIcon, Storage as BackfillIcon,
} from '@mui/icons-material';
import { adminApi, handleAdminApiError } from '../../services/adminApi';

const OTPPhoneView = () => {
    const navigate = useNavigate();

    // List state
    const [phones, setPhones] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [total, setTotal] = useState(0);
    const [nextCursor, setNextCursor] = useState(null);
    const [search, setSearch] = useState('');

    // Dialogs
    const [reuseDialog, setReuseDialog] = useState({ open: false, phone: null });
    const [releaseDialog, setReleaseDialog] = useState({ open: false, phone: null, owners: [] });
    const [detailDialog, setDetailDialog] = useState({ open: false, phone: null, data: null });

    // Reuse form
    const [reuseMaxUses, setReuseMaxUses] = useState(2);
    const [reuseNote, setReuseNote] = useState('');
    const [reuseLoading, setReuseLoading] = useState(false);

    // Release form
    const [releaseUid, setReleaseUid] = useState('');
    const [releaseLoading, setReleaseLoading] = useState(false);

    // Snackbar
    const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

    const fetchPhones = useCallback(async (cursor = null) => {
        setLoading(true);
        setError(null);
        try {
            const params = { limit: 50 };
            if (cursor) params.cursor = cursor;
            if (search) params.search = search;

            const data = await adminApi.getOTPPhones(params);
            if (cursor) {
                setPhones(prev => [...prev, ...(data.items || [])]);
            } else {
                setPhones(data.items || []);
            }
            setNextCursor(data.nextCursor || null);
            setTotal(data.total || 0);
        } catch (err) {
            setError(handleAdminApiError(err).message);
        } finally {
            setLoading(false);
        }
    }, [search]);

    useEffect(() => {
        fetchPhones();
    }, [fetchPhones]);

    const handleSearch = () => {
        setNextCursor(null);
        fetchPhones(null);
    };

    const handleLoadMore = () => {
        if (nextCursor) fetchPhones(nextCursor);
    };

    // ─── Detail Dialog ────────────────────────────────────────────────────

    const openDetail = async (phoneNumber) => {
        setDetailDialog({ open: true, phone: phoneNumber, data: null });
        try {
            const data = await adminApi.getOTPPhoneDetail(phoneNumber);
            setDetailDialog({ open: true, phone: phoneNumber, data });
        } catch (err) {
            setSnackbar({ open: true, message: handleAdminApiError(err).message, severity: 'error' });
            setDetailDialog({ open: false, phone: null, data: null });
        }
    };

    // ─── Reuse Dialog ─────────────────────────────────────────────────────

    const openReuseDialog = (item) => {
        setReuseMaxUses(item.maxUses > 1 ? item.maxUses : 2);
        setReuseNote(item.reuseNote || '');
        setReuseDialog({ open: true, phone: item.phoneNumber });
    };

    const handleSetReuse = async () => {
        setReuseLoading(true);
        try {
            await adminApi.setPhoneReuse(reuseDialog.phone, { maxUses: reuseMaxUses, note: reuseNote });
            setSnackbar({ open: true, message: `Reuse allowance set to ${reuseMaxUses} for ${reuseDialog.phone}`, severity: 'success' });
            setReuseDialog({ open: false, phone: null });
            fetchPhones(null);
        } catch (err) {
            setSnackbar({ open: true, message: handleAdminApiError(err).message, severity: 'error' });
        } finally {
            setReuseLoading(false);
        }
    };

    const handleRemoveReuse = async (phoneNumber) => {
        try {
            await adminApi.deletePhoneReuse(phoneNumber);
            setSnackbar({ open: true, message: 'Reuse allowance removed', severity: 'success' });
            fetchPhones(null);
        } catch (err) {
            setSnackbar({ open: true, message: handleAdminApiError(err).message, severity: 'error' });
        }
    };

    // ─── Release Dialog ───────────────────────────────────────────────────

    const openReleaseDialog = (item) => {
        setReleaseUid('');
        setReleaseDialog({ open: true, phone: item.phoneNumber, owners: item.owners || [] });
    };

    const handleRelease = async () => {
        setReleaseLoading(true);
        try {
            await adminApi.releasePhone(releaseDialog.phone, releaseUid || null);
            setSnackbar({ open: true, message: `Phone ${releaseDialog.phone} released successfully`, severity: 'success' });
            setReleaseDialog({ open: false, phone: null, owners: [] });
            fetchPhones(null);
        } catch (err) {
            setSnackbar({ open: true, message: handleAdminApiError(err).message, severity: 'error' });
        } finally {
            setReleaseLoading(false);
        }
    };

    // ─── Backfill ─────────────────────────────────────────────────────────

    const [backfillLoading, setBackfillLoading] = useState(false);

    const handleBackfill = async () => {
        setBackfillLoading(true);
        try {
            const result = await adminApi.backfillPhoneRegistry();
            setSnackbar({ open: true, message: `Backfill complete: ${result.backfilled} phones indexed`, severity: 'success' });
            fetchPhones(null);
        } catch (err) {
            setSnackbar({ open: true, message: handleAdminApiError(err).message, severity: 'error' });
        } finally {
            setBackfillLoading(false);
        }
    };

    return (
        <Box sx={{ p: 3, maxWidth: 1600, margin: '0 auto' }} data-testid="otp-phone-view">
            {/* Header */}
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 3, alignItems: 'center' }}>
                <Box>
                    <Typography variant="h5" fontWeight="bold">OTP Phone Numbers</Typography>
                    <Typography variant="body2" color="text.secondary">
                        {total} registered phone{total !== 1 ? 's' : ''}
                    </Typography>
                </Box>
                <Stack direction="row" spacing={2}>
                    <Button
                        startIcon={<BackfillIcon />}
                        onClick={handleBackfill}
                        variant="outlined"
                        disabled={backfillLoading}
                        data-testid="otp-phones-btn-backfill"
                    >
                        {backfillLoading ? 'Backfilling...' : 'Backfill Registry'}
                    </Button>
                    <Button
                        startIcon={<RefreshIcon />}
                        onClick={() => fetchPhones(null)}
                        variant="outlined"
                        disabled={loading}
                        data-testid="otp-phones-btn-refresh"
                    >
                        Refresh
                    </Button>
                </Stack>
            </Box>

            {/* Search */}
            <Paper sx={{ p: 2, mb: 3 }}>
                <Grid container spacing={2} alignItems="center">
                    <Grid item xs={12} md={8}>
                        <TextField
                            fullWidth
                            label="Search by phone number"
                            size="small"
                            value={search}
                            onChange={(e) => setSearch(e.target.value)}
                            onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
                            InputProps={{
                                startAdornment: <PhoneIcon color="action" sx={{ mr: 1, fontSize: 20 }} />
                            }}
                            data-testid="otp-phones-search"
                        />
                    </Grid>
                    <Grid item xs={12} md={4}>
                        <Button
                            fullWidth
                            variant="contained"
                            startIcon={<SearchIcon />}
                            onClick={handleSearch}
                            data-testid="otp-phones-btn-search"
                        >
                            Search
                        </Button>
                    </Grid>
                </Grid>
            </Paper>

            {error && (
                <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
                    {error}
                </Alert>
            )}

            {/* Phone Table */}
            <TableContainer component={Paper} elevation={2} data-testid="otp-phones-table">
                <Table size="small">
                    <TableHead sx={{ bgcolor: 'grey.50' }}>
                        <TableRow>
                            <TableCell>Phone</TableCell>
                            <TableCell>Owner(s)</TableCell>
                            <TableCell align="center">Owner Count</TableCell>
                            <TableCell align="center">Max Uses</TableCell>
                            <TableCell>Registered</TableCell>
                            <TableCell align="right">Actions</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {loading && phones.length === 0 ? (
                            <TableRow>
                                <TableCell colSpan={6} align="center" sx={{ py: 5 }}>
                                    <CircularProgress />
                                </TableCell>
                            </TableRow>
                        ) : phones.length === 0 ? (
                            <TableRow>
                                <TableCell colSpan={6} align="center" sx={{ py: 3 }}>
                                    <Typography color="text.secondary">No phones found</Typography>
                                </TableCell>
                            </TableRow>
                        ) : (
                            phones.map((item) => (
                                <TableRow key={item.phoneNumber} hover data-testid={`otp-phone-row-${item.maskedPhone}`}>
                                    <TableCell>
                                        <Tooltip title="Click for full details">
                                            <Typography
                                                variant="body2"
                                                fontFamily="monospace"
                                                sx={{ cursor: 'pointer', color: 'primary.main', '&:hover': { textDecoration: 'underline' } }}
                                                onClick={() => openDetail(item.phoneNumber)}
                                                data-testid="otp-phone-masked"
                                            >
                                                {item.maskedPhone}
                                            </Typography>
                                        </Tooltip>
                                    </TableCell>
                                    <TableCell>
                                        <Stack spacing={0.5}>
                                            {(item.owners || []).slice(0, 3).map((uid) => (
                                                <Typography
                                                    key={uid}
                                                    variant="caption"
                                                    component="a"
                                                    href={`/users/${uid}`}
                                                    onClick={(e) => { e.preventDefault(); navigate(`/users/${uid}`); }}
                                                    sx={{ color: 'primary.main', textDecoration: 'none', '&:hover': { textDecoration: 'underline' }, fontFamily: 'monospace' }}
                                                    noWrap
                                                    title={uid}
                                                    data-testid="otp-phone-owner-link"
                                                >
                                                    {uid.length > 20 ? uid.slice(0, 20) + '...' : uid}
                                                </Typography>
                                            ))}
                                            {(item.owners || []).length > 3 && (
                                                <Typography variant="caption" color="text.secondary">
                                                    +{item.owners.length - 3} more
                                                </Typography>
                                            )}
                                        </Stack>
                                    </TableCell>
                                    <TableCell align="center">
                                        <Chip label={item.ownerCount} size="small" variant="outlined" />
                                    </TableCell>
                                    <TableCell align="center">
                                        <Chip
                                            label={item.maxUses}
                                            size="small"
                                            color={item.maxUses > 1 ? 'warning' : 'default'}
                                            variant={item.maxUses > 1 ? 'filled' : 'outlined'}
                                        />
                                    </TableCell>
                                    <TableCell>
                                        <Typography variant="body2" fontFamily="monospace">
                                            {item.registeredAt ? new Date(item.registeredAt).toLocaleDateString() : '-'}
                                        </Typography>
                                    </TableCell>
                                    <TableCell align="right">
                                        <Stack direction="row" spacing={0.5} justifyContent="flex-end">
                                            <Tooltip title="View Details">
                                                <IconButton size="small" onClick={() => openDetail(item.phoneNumber)} data-testid="otp-phone-btn-detail">
                                                    <ViewIcon fontSize="small" />
                                                </IconButton>
                                            </Tooltip>
                                            <Tooltip title="Edit Reuse">
                                                <IconButton size="small" onClick={() => openReuseDialog(item)} data-testid="otp-phone-btn-reuse">
                                                    <EditIcon fontSize="small" />
                                                </IconButton>
                                            </Tooltip>
                                            {item.maxUses > 1 && (
                                                <Tooltip title="Remove Reuse">
                                                    <IconButton size="small" onClick={() => handleRemoveReuse(item.phoneNumber)} data-testid="otp-phone-btn-remove-reuse">
                                                        <DeleteIcon fontSize="small" color="warning" />
                                                    </IconButton>
                                                </Tooltip>
                                            )}
                                            <Tooltip title="Release Phone">
                                                <IconButton size="small" onClick={() => openReleaseDialog(item)} data-testid="otp-phone-btn-release">
                                                    <ReleaseIcon fontSize="small" color="error" />
                                                </IconButton>
                                            </Tooltip>
                                        </Stack>
                                    </TableCell>
                                </TableRow>
                            ))
                        )}
                    </TableBody>
                </Table>
            </TableContainer>

            {nextCursor && (
                <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3 }}>
                    <Button disabled={loading} onClick={handleLoadMore} variant="text">
                        {loading ? 'Loading...' : 'Load More'}
                    </Button>
                </Box>
            )}

            {/* ─── Reuse Dialog ──────────────────────────────────────────────── */}
            <Dialog
                open={reuseDialog.open}
                onClose={() => setReuseDialog({ open: false, phone: null })}
                maxWidth="sm"
                fullWidth
                data-testid="otp-phone-reuse-dialog"
            >
                <DialogTitle>Set Phone Reuse Allowance</DialogTitle>
                <DialogContent>
                    <Box sx={{ pt: 1 }}>
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                            Allow this phone number to be used by multiple accounts.
                        </Typography>
                        <TextField
                            fullWidth
                            label="Max Uses"
                            type="number"
                            value={reuseMaxUses}
                            onChange={(e) => setReuseMaxUses(Math.max(2, parseInt(e.target.value) || 2))}
                            inputProps={{ min: 2 }}
                            sx={{ mb: 2 }}
                            data-testid="otp-phone-reuse-max-uses"
                        />
                        <TextField
                            fullWidth
                            label="Reason / Note (required)"
                            multiline
                            rows={2}
                            value={reuseNote}
                            onChange={(e) => setReuseNote(e.target.value)}
                            data-testid="otp-phone-reuse-note"
                        />
                    </Box>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setReuseDialog({ open: false, phone: null })} disabled={reuseLoading}>
                        Cancel
                    </Button>
                    <Button
                        onClick={handleSetReuse}
                        variant="contained"
                        disabled={reuseLoading || !reuseNote.trim()}
                        data-testid="otp-phone-reuse-submit"
                    >
                        {reuseLoading ? 'Saving...' : 'Set Reuse'}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* ─── Release Dialog ────────────────────────────────────────────── */}
            <Dialog
                open={releaseDialog.open}
                onClose={() => setReleaseDialog({ open: false, phone: null, owners: [] })}
                maxWidth="sm"
                fullWidth
                data-testid="otp-phone-release-dialog"
            >
                <DialogTitle>Release Phone Number</DialogTitle>
                <DialogContent>
                    <Box sx={{ pt: 1 }}>
                        <Alert severity="warning" sx={{ mb: 2 }}>
                            Released users will need to re-verify their phone number to claim a free trial again.
                        </Alert>
                        {releaseDialog.owners.length > 1 && (
                            <FormControl fullWidth sx={{ mb: 2 }}>
                                <InputLabel>Release specific owner (optional)</InputLabel>
                                <Select
                                    value={releaseUid}
                                    onChange={(e) => setReleaseUid(e.target.value)}
                                    label="Release specific owner (optional)"
                                    data-testid="otp-phone-release-uid-select"
                                >
                                    <MenuItem value="">All owners</MenuItem>
                                    {releaseDialog.owners.map((uid) => (
                                        <MenuItem key={uid} value={uid}>{uid}</MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        )}
                        <Typography variant="body2" color="text.secondary">
                            {releaseUid
                                ? `This will release ${releaseUid} from this phone number.`
                                : `This will release all ${releaseDialog.owners.length} owner(s) from this phone number.`
                            }
                        </Typography>
                    </Box>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setReleaseDialog({ open: false, phone: null, owners: [] })} disabled={releaseLoading}>
                        Cancel
                    </Button>
                    <Button
                        onClick={handleRelease}
                        variant="contained"
                        color="error"
                        disabled={releaseLoading}
                        data-testid="otp-phone-release-submit"
                    >
                        {releaseLoading ? 'Releasing...' : 'Release'}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* ─── Detail Dialog ──────────────────────────────────────────────── */}
            <Dialog
                open={detailDialog.open}
                onClose={() => setDetailDialog({ open: false, phone: null, data: null })}
                maxWidth="sm"
                fullWidth
                data-testid="otp-phone-detail-dialog"
            >
                <DialogTitle>Phone Number Details</DialogTitle>
                <DialogContent>
                    {!detailDialog.data ? (
                        <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
                            <CircularProgress />
                        </Box>
                    ) : (
                        <Box sx={{ pt: 1 }}>
                            <Typography variant="subtitle2" gutterBottom>Full Phone Number</Typography>
                            <Typography variant="body1" fontFamily="monospace" sx={{ mb: 2 }} data-testid="otp-phone-detail-number">
                                {detailDialog.data.phoneNumber}
                            </Typography>

                            <Typography variant="subtitle2" gutterBottom>Max Uses</Typography>
                            <Typography variant="body1" sx={{ mb: 1 }}>
                                {detailDialog.data.maxUses}
                                {detailDialog.data.reuseNote && (
                                    <Typography variant="caption" color="text.secondary" sx={{ ml: 1 }}>
                                        ({detailDialog.data.reuseNote})
                                    </Typography>
                                )}
                            </Typography>

                            <Typography variant="subtitle2" gutterBottom sx={{ mt: 2 }}>Registered</Typography>
                            <Typography variant="body1" sx={{ mb: 2 }}>
                                {detailDialog.data.registeredAt ? new Date(detailDialog.data.registeredAt).toLocaleString() : 'Unknown'}
                            </Typography>

                            <Typography variant="subtitle2" gutterBottom>
                                Owners ({detailDialog.data.ownerCount})
                            </Typography>
                            {(detailDialog.data.owners || []).map((owner) => (
                                <Paper key={owner.uid} sx={{ p: 1.5, mb: 1, bgcolor: 'grey.50' }}>
                                    <Typography
                                        variant="body2"
                                        component="a"
                                        href={`/users/${owner.uid}`}
                                        onClick={(e) => { e.preventDefault(); navigate(`/users/${owner.uid}`); setDetailDialog({ open: false, phone: null, data: null }); }}
                                        sx={{ color: 'primary.main', textDecoration: 'none', '&:hover': { textDecoration: 'underline' }, fontWeight: 500 }}
                                        data-testid="otp-phone-detail-owner-link"
                                    >
                                        {owner.uid}
                                    </Typography>
                                    <Typography variant="caption" color="text.secondary" display="block">
                                        {owner.email || 'No email'}
                                    </Typography>
                                </Paper>
                            ))}
                        </Box>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setDetailDialog({ open: false, phone: null, data: null })}>
                        Close
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Snackbar */}
            <Snackbar
                open={snackbar.open}
                autoHideDuration={6000}
                onClose={() => setSnackbar({ ...snackbar, open: false })}
                data-testid="otp-phones-snackbar"
            >
                <Alert severity={snackbar.severity} onClose={() => setSnackbar({ ...snackbar, open: false })}>
                    {snackbar.message}
                </Alert>
            </Snackbar>
        </Box>
    );
};

export default OTPPhoneView;
